/**
 * Minimap Component - React component for graphical mode minimap overlay
 * Displays player position, NPCs, and location boundaries
 */

import React, { useEffect, useRef, useState } from 'react';
import { useGameStore } from '@/state/gameStore';
import type { RailwayNPCData } from '@/services/railwayWorldStateService';
import { locationService } from '@/services/locationService';

interface MinimapProps {
  isVisible: boolean;
  playerPosition?: { x: number; y: number };
  npcs?: RailwayNPCData[];
  mapWidth?: number;
  mapHeight?: number;
}

export const Minimap: React.FC<MinimapProps> = ({
  isVisible,
  playerPosition = { x: 400, y: 300 },
  npcs = [],
  mapWidth = 1600,
  mapHeight = 1200,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const currentPlayerState = useGameStore((state) => state.currentPlayerState);
  const [scale, setScale] = useState(0.1); // Scale factor for minimap

  useEffect(() => {
    if (!isVisible || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = 200;
    canvas.height = 150;

    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw map boundary
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 2;
    ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);

    // Calculate scale
    const scaleX = (canvas.width - 20) / mapWidth;
    const scaleY = (canvas.height - 20) / mapHeight;
    const minScale = Math.min(scaleX, scaleY);

    // Draw NPCs
    npcs.forEach((npc) => {
      if (npc.position) {
        const x = npc.position.x * minScale + 10;
        const y = npc.position.y * minScale + 10;

        // Color based on karma
        if (npc.karma > 0) {
          ctx.fillStyle = '#00ff00'; // Green
        } else if (npc.karma < 0) {
          ctx.fillStyle = '#ff0000'; // Red
        } else {
          ctx.fillStyle = '#ffffff'; // White
        }

        ctx.fillRect(x - 2, y - 2, 4, 4);
      }
    });

    // Draw player
    const playerX = playerPosition.x * minScale + 10;
    const playerY = playerPosition.y * minScale + 10;

    ctx.fillStyle = '#00ffff'; // Cyan
    ctx.beginPath();
    ctx.arc(playerX, playerY, 4, 0, Math.PI * 2);
    ctx.fill();

    // Draw player direction indicator
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(playerX, playerY);
    ctx.lineTo(playerX, playerY - 6);
    ctx.stroke();

    // Draw location label
    const location = currentPlayerState?.location || locationService.getCurrentLocation();
    ctx.fillStyle = '#ffffff';
    ctx.font = '10px monospace';
    ctx.fillText(location.replace(/-/g, ' '), 10, 15);
  }, [isVisible, playerPosition, npcs, mapWidth, mapHeight, currentPlayerState]);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 bg-slate-900/90 border border-cyan-500/40 rounded-lg p-2 z-50 shadow-lg">
      <canvas
        ref={canvasRef}
        className="block"
        style={{ width: '200px', height: '150px' }}
      />
      <div className="text-xs text-slate-400 mt-1 text-center">
        Press M to toggle minimap
      </div>
    </div>
  );
};

